import type { WidgetPublicConfig } from '@studiof-agents/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  type ChatEvent,
  type WidgetMode,
  fetchConfig,
  fetchHistory,
  modeIdentity,
  streamChat,
} from '../lib/api';
import { Markdown } from '../lib/markdown';
import {
  type StoredConversation,
  getOrCreateConversation,
  getStoredOpenState,
  resetConversation,
  setStoredOpenState,
  updateStoredConversation,
} from '../lib/session';

type Message = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  pending?: boolean;
};

type Props = {
  mode: WidgetMode;
  /**
   * Force le chat ouvert au montage (cas démo via `data-open="true"`).
   * Si false/absent, on respecte l'état sessionStorage comme avant.
   */
  openByDefault?: boolean;
};

const STUDIO_F_HREF = 'https://studiof.pro/sites-et-agents?ref=widget';
const STUDIO_F_CONTACT_EMAIL = 'flavien@studiof.pro';

function uid(): string {
  return Math.random().toString(36).slice(2, 11);
}

export function App({ mode, openByDefault = false }: Props) {
  const [config, setConfig] = useState<WidgetPublicConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(() =>
    openByDefault ? true : getStoredOpenState(),
  );
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  // Identifiant stable pour la session : slug en prod, agentId en démo.
  // Évite que démo + embed prod sur le même browser partagent la même session.
  const identity = useMemo(() => modeIdentity(mode), [mode]);
  // Source de vérité côté client : un seul objet localStorage avec session_id,
  // conversation_id (peuplé après le premier échange), token (idem) et un TTL
  // unique géré par session.ts. La ref garde la version "live" lue par les
  // callbacks (sendMessage, reset) sans déclencher de re-render à chaque
  // changement.
  const conversationRef = useRef<StoredConversation>(getOrCreateConversation(identity));
  const messagesRef = useRef<HTMLDivElement | null>(null);
  // Empêche le double-fetch de l'historique : sans ce flag, un parent qui
  // rerendrait App (changement de mode très improbable mais possible) ferait
  // refetcher l'historique alors qu'il a déjà été chargé.
  const hasHydratedRef = useRef(false);

  // Layout courant (defaut 'bubble' tant que config n'a pas charge). Les
  // hooks plus bas (ESC, body scroll lock) en dependent pour adapter leur
  // comportement par layout. isInline coupe le bouton fermer, l'ESC et le
  // lock dans tous les cas (le chat est permanent dans la page hote).
  const layout = config?.branding.widget_layout ?? 'bubble';
  const isInline = layout === 'inline';

  // Track viewport mobile via matchMedia (max-width: 480px). Sert a basculer
  // entre les overrides _mobile (Migration 021) et les valeurs desktop. SSR
  // safe : on initialise a false, la valeur reelle s'applique au mount.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 480px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Fetch config au montage. Le mode est dans les deps : si jamais on change
  // de mode (cas non actuel mais possible si on étend l'API), le widget se
  // re-configure proprement.
  useEffect(() => {
    let cancelled = false;
    fetchConfig(mode)
      .then((cfg) => {
        if (!cancelled) setConfig(cfg);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setConfigError(err instanceof Error ? err.message : 'Configuration unavailable');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  // Réhydratation de l'historique au montage : si on a un (conversation_id, token)
  // valide en localStorage, on demande au serveur les messages stockés et on
  // seed le state. Cela évite au visiteur de retomber sur un chat vide après
  // une navigation. En mode démo, on ne réhydrate pas (chaque visite démo doit
  // repartir vierge, ce qui est aussi enforcé par openByDefault).
  // biome-ignore lint/correctness/useExhaustiveDependencies: identity est calculé depuis mode déjà dans les deps, double-déclencher serait redondant
  useEffect(() => {
    if (hasHydratedRef.current) return;
    // demo et preview : on ne rehydrate pas. Pour demo, chaque visite doit
    // partir vierge. Pour preview, on n'envoie de toute facon pas de message,
    // pas d'historique a recharger.
    if (mode.kind === 'demo' || mode.kind === 'preview') {
      hasHydratedRef.current = true;
      return;
    }
    const { conversation_id: convId, token } = conversationRef.current;
    if (!convId || !token) {
      hasHydratedRef.current = true;
      return;
    }
    let cancelled = false;
    fetchHistory(convId, token).then((history) => {
      if (cancelled) return;
      hasHydratedRef.current = true;
      if (!history) {
        // Token refusé ou conv introuvable : on purge le localStorage et on
        // repart sur une session vierge. Le serveur reste source de vérité.
        conversationRef.current = resetConversation(identity);
        return;
      }
      // Seed du state avec l'historique serveur. Les IDs serveur sont des UUID,
      // le widget les utilise tels quels pour la key React.
      setMessages(
        history.messages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
        })),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  // Scroll auto en bas à chaque nouveau message (length suffit, on n'écoute pas le contenu)
  // biome-ignore lint/correctness/useExhaustiveDependencies: messages.length déclenche le scroll, le contenu détaillé n'est pas une dep nécessaire
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages.length]);

  // Persiste l'état open dans sessionStorage. En démo (openByDefault), on évite
  // de surcharger le storage : la démo est éphémère par nature, et un rechargement
  // doit toujours ré-ouvrir le chat.
  useEffect(() => {
    if (!openByDefault) {
      setStoredOpenState(isOpen);
    }
  }, [isOpen, openByDefault]);

  // Fermer avec Échap : standard modal pattern. Sert principalement aux clients
  // qui testent depuis un clavier physique (desktop). Sur mobile, la touche
  // n'existe pas, donc no-op silencieux. Pas applique au layout 'inline' (le
  // chat est permanent, il n'y a pas de notion de fermer).
  useEffect(() => {
    if (!isOpen || isInline) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, isInline]);

  // Lock du scroll du site host quand le chat couvre tout ou partie du
  // viewport. Sans ça, le visiteur peut scroller la page hôte derrière le
  // chat (les gestures pénètrent même si le viewport est couvert).
  //
  // Regle par layout :
  // - modal + sheet : toujours lock (la window est devant le contenu)
  // - bubble + drawer : lock seulement en mobile fullscreen (sinon la window
  //   est en coin, le visiteur peut vouloir scroller en parallele)
  // - inline : jamais lock (le chat vit dans le flow de la page)
  useEffect(() => {
    if (!isOpen || isInline) return;
    if (typeof window === 'undefined') return;
    const isMobile = window.matchMedia('(max-width: 480px)').matches;
    const shouldLock = layout === 'modal' || layout === 'sheet' || isMobile;
    if (!shouldLock) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, isInline, layout]);

  // Cap démo : on compte les messages assistant non-pending. Quand atteint, on
  // verrouille l'input. Le serveur a un garde-fou identique côté DB pour les
  // cas où un visiteur contournerait le widget.
  const assistantCount = useMemo(
    () => messages.filter((m) => m.role === 'assistant' && !m.pending).length,
    [messages],
  );
  const demoCap = config?.demo_cap;
  const demoCapReached = demoCap !== undefined && assistantCount >= demoCap;

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming || demoCapReached) return;

      setChatError(null);

      const userMessage: Message = { id: uid(), role: 'user', content: trimmed };
      const assistantPlaceholder: Message = {
        id: uid(),
        role: 'assistant',
        content: '',
        pending: true,
      };

      setMessages((prev) => [...prev, userMessage, assistantPlaceholder]);
      setInput('');
      setIsStreaming(true);

      try {
        for await (const event of streamChat(mode, {
          session_id: conversationRef.current.session_id,
          message: trimmed,
          website: honeypot,
        }) as AsyncGenerator<ChatEvent>) {
          if (event.type === 'meta') {
            // Premier frame du serveur : on récupère l'identifiant stable de
            // la conversation et son token de lecture. Les deux sont persistés
            // tout de suite en localStorage pour que la prochaine page charge
            // directement l'historique via fetchHistory.
            conversationRef.current = {
              ...conversationRef.current,
              conversation_id: event.conversation_id,
              token: event.token,
            };
            updateStoredConversation(identity, {
              conversation_id: event.conversation_id,
              token: event.token,
            });
          } else if (event.type === 'token') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantPlaceholder.id
                  ? { ...m, content: m.content + event.delta, pending: true }
                  : m,
              ),
            );
          } else if (event.type === 'done') {
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantPlaceholder.id ? { ...m, pending: false } : m)),
            );
          } else if (event.type === 'error') {
            setChatError(event.message);
            setMessages((prev) => prev.filter((m) => m.id !== assistantPlaceholder.id));
          }
        }
      } catch (err) {
        setChatError(err instanceof Error ? err.message : 'Erreur réseau');
        setMessages((prev) => prev.filter((m) => m.id !== assistantPlaceholder.id));
      } finally {
        setIsStreaming(false);
      }
    },
    [mode, isStreaming, honeypot, demoCapReached, identity],
  );

  // Bouton « Nouvelle conversation » dans le header. Reset complet : nouveau
  // session_id côté localStorage (l'entrée est purgée puis regénérée), state
  // des messages vidé, erreurs effacées. Le serveur créera donc une nouvelle
  // ligne conversations au prochain message.
  const handleNewConversation = useCallback(() => {
    if (isStreaming) return;
    conversationRef.current = resetConversation(identity);
    setMessages([]);
    setChatError(null);
    setInput('');
  }, [identity, isStreaming]);

  if (configError) {
    // Log toujours pour Flavien (et le client) si console ouverte.
    console.error('[StudioFAgents] Config error:', configError);
    // ConfigErrorBanner : bandeau discret bottom-right, dismissible. Sert de
    // signal visible à l'intégrateur (Flavien) sans casser l'expérience du
    // visiteur final (texte court, neutre, fermable). Évite l'incident type
    // Holiclay où le widget meurt en silence côté client.
    return <ConfigErrorBanner error={configError} mode={mode} />;
  }

  if (!config) {
    return null; // pas encore chargé, on n'affiche rien (pas de flash)
  }

  // Migration 021 : position dissociee mobile. Si mobile ET valeur _mobile
  // non-nulle, l'utiliser ; sinon fallback au desktop. Pareil pour les offsets.
  const effectivePosition =
    isMobile && config.branding.bubble_position_mobile
      ? config.branding.bubble_position_mobile
      : config.branding.bubble_position;
  const effectiveOffsetX =
    isMobile && config.branding.position_offset_x_px_mobile != null
      ? config.branding.position_offset_x_px_mobile
      : (config.branding.position_offset_x_px ?? 24);
  const effectiveOffsetY =
    isMobile && config.branding.position_offset_y_px_mobile != null
      ? config.branding.position_offset_y_px_mobile
      : (config.branding.position_offset_y_px ?? 24);

  const positionClass = `sfa-pos-${effectivePosition}`;
  const radiusClass = `sfa-radius-${config.branding.corner_radius}`;
  // Migration 019, consume PR : applique la personnalite visuelle. Default
  // 'classic' = pas de surcharge CSS, comportement historique inchange.
  const designClass = `sfa-design-${config.branding.widget_design ?? 'classic'}`;
  // layout + isInline sont declares en haut du component (avant les useEffect
  // qui en dependent), juste les classes derivees ici.
  const layoutClass = `sfa-layout-${layout}`;
  // Inline force le chat ouvert en permanence : pas de bulle, pas de bouton
  // fermer, le widget vit dans le flow de la page hote.
  const effectiveIsOpen = isInline || isOpen;
  // bubble_shape default = circle (rétro-compat). Pas de classe nécessaire pour
  // circle car c'est le default CSS. On n'ajoute la classe que pour rounded/square.
  const bubbleShapeClass =
    config.branding.bubble_shape !== 'circle'
      ? `sfa-bubble-shape-${config.branding.bubble_shape}`
      : '';
  const rootStyle = {
    '--sfa-primary': config.branding.primary_color,
    '--sfa-secondary': config.branding.secondary_color,
    '--sfa-font': `${config.branding.font_family}, system-ui, -apple-system, sans-serif`,
    // Migration 020 + 021 : offsets configurables (px), avec dissociation
    // mobile possible. effectiveOffsetX/Y choisissent deja desktop ou mobile
    // selon le viewport en cours, plus rien a faire ici.
    '--sfa-offset-x': `${effectiveOffsetX}px`,
    '--sfa-offset-y': `${effectiveOffsetY}px`,
  } as Record<string, string>;

  return (
    <div
      className={`sfa-root ${positionClass} ${radiusClass} ${designClass} ${layoutClass}`}
      style={rootStyle}
    >
      {/*
        Bulle toujours visible (sauf layout 'inline' ou le chat est permanent
        dans la page et n'a pas de notion d'ouverture). Clic pour ouvrir/fermer.
        Quand le chat est ouvert, on remplace l'icone chat par une croix qui
        sert de bouton de fermeture rapide, complementaire de la croix du
        header de la window pour les layouts ou le header est visible.
      */}
      {!isInline && (
        <button
          type="button"
          className={`sfa-bubble ${bubbleShapeClass} ${effectiveIsOpen ? 'sfa-bubble--open' : ''}`.trim()}
          aria-label={effectiveIsOpen ? 'Fermer le chat' : config.branding.welcome_message}
          aria-expanded={effectiveIsOpen}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          {effectiveIsOpen ? (
            // Croix de fermeture quand le chat est ouvert.
            <svg
              viewBox="0 0 24 24"
              className="sfa-bubble-icon sfa-bubble-icon--close"
              aria-hidden="true"
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          ) : config.branding.bubble_icon_url ? (
            <img src={config.branding.bubble_icon_url} alt="" className="sfa-bubble-img" />
          ) : (
            <svg viewBox="0 0 24 24" className="sfa-bubble-icon" aria-hidden="true">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
            </svg>
          )}
        </button>
      )}

      {/* Backdrop : seulement pour le layout 'modal'. Click ferme la window
          (comportement modale standard). Pas applique aux autres layouts pour
          ne pas casser le bottom sheet (qui doit garder la page derriere
          interactive) ou le drawer (idem). */}
      {effectiveIsOpen && layout === 'modal' && (
        <button
          type="button"
          className="sfa-backdrop"
          aria-label="Fermer la fenêtre"
          onClick={() => setIsOpen(false)}
        />
      )}

      {effectiveIsOpen && (
        // biome-ignore lint/a11y/useSemanticElements: <dialog> natif a un comportement modal incompatible avec une widget non-bloquante
        <div className="sfa-window" role="dialog" aria-label="Chat assistance">
          <header className="sfa-header">
            <div className="sfa-header-info">
              {config.branding.chat_avatar_url && (
                <img src={config.branding.chat_avatar_url} alt="" className="sfa-header-avatar" />
              )}
              <div className="sfa-header-text">
                <div className="sfa-header-title">
                  {config.branding.bot_name?.trim() || 'Assistant'}
                </div>
                {config.branding.header_subtitle && (
                  <div className="sfa-header-subtitle">{config.branding.header_subtitle}</div>
                )}
              </div>
            </div>
            <div className="sfa-header-actions">
              {/* Nouvelle conversation : visible uniquement s'il y a déjà des
                  messages ou un id de conv stocké. Évite de polluer le header
                  pour les visites fraîches où le bouton n'a aucun sens. */}
              {(messages.length > 0 || conversationRef.current.conversation_id) && (
                <button
                  type="button"
                  className="sfa-header-action"
                  aria-label="Nouvelle conversation"
                  title="Nouvelle conversation"
                  onClick={handleNewConversation}
                  disabled={isStreaming}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M21 12a9 9 0 1 1-3-6.7" />
                    <polyline points="21 4 21 10 15 10" />
                  </svg>
                </button>
              )}
              {/* Bouton fermer : cache en layout 'inline' (le chat vit en
                  permanence dans la page hote, pas de notion de fermer). */}
              {!isInline && (
                <button
                  type="button"
                  className="sfa-close"
                  aria-label="Fermer"
                  onClick={() => setIsOpen(false)}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              )}
            </div>
          </header>

          <div className="sfa-messages" ref={messagesRef}>
            {messages.length === 0 && (
              <div className="sfa-message sfa-message-assistant">
                <div className="sfa-message-bubble">{config.branding.welcome_message}</div>
              </div>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                className={`sfa-message ${m.role === 'user' ? 'sfa-message-user' : 'sfa-message-assistant'}`}
              >
                <div className="sfa-message-bubble">
                  {m.content ? (
                    // Les réponses de l'agent peuvent contenir du markdown
                    // (gras, listes, titres) : on le rend. Le message du
                    // visiteur reste en texte brut (on n'interprète jamais son
                    // input comme du markdown).
                    m.role === 'assistant' ? (
                      <Markdown text={m.content} />
                    ) : (
                      m.content
                    )
                  ) : (
                    m.pending && <Typing />
                  )}
                </div>
              </div>
            ))}
          </div>

          {chatError && <div className="sfa-error">{chatError}</div>}

          {messages.length === 0 && config.conversation_starters.length > 0 && (
            <div className="sfa-starters">
              {config.conversation_starters.slice(0, 4).map((starter) => (
                <button
                  key={starter}
                  type="button"
                  className="sfa-starter"
                  onClick={() => sendMessage(starter)}
                >
                  {starter}
                </button>
              ))}
            </div>
          )}

          {config.cta_buttons.length > 0 && (
            <div className="sfa-ctas">
              {config.cta_buttons.map((cta) => (
                <a
                  key={cta.url}
                  href={cta.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sfa-cta"
                >
                  {cta.label}
                </a>
              ))}
            </div>
          )}

          {demoCapReached ? (
            // En démo, cap atteint : on remplace la barre de saisie par un
            // récap visuel "Démo terminée" + un lien direct vers le contact
            // commercial. Le serveur a aussi un garde-fou en DB, donc même un
            // visiteur qui contourne le widget tombe sur le message de fin.
            <div className="sfa-demo-end">
              Cette démo est limitée à {demoCap} échanges. Pour aller plus loin :{' '}
              <a href={`mailto:${STUDIO_F_CONTACT_EMAIL}`}>{STUDIO_F_CONTACT_EMAIL}</a>
            </div>
          ) : (
            <form
              className="sfa-input-bar"
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage(input);
              }}
            >
              {/* Honeypot anti-bot. Caché en CSS, jamais focusable, aria-hidden.
                  Si un bot le remplit, le serveur rejette la requête en 400.
                  En mode démo le honeypot est envoyé mais ignoré côté serveur. */}
              <input
                type="text"
                name="website"
                className="sfa-honeypot"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={honeypot}
                onInput={(e) => setHoneypot((e.target as HTMLInputElement).value)}
              />
              <textarea
                className="sfa-input"
                rows={1}
                placeholder={config.branding.placeholder_text}
                value={input}
                onInput={(e) => setInput((e.target as HTMLTextAreaElement).value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
                disabled={isStreaming}
              />
              <button
                type="submit"
                className="sfa-send"
                aria-label="Envoyer"
                disabled={!input.trim() || isStreaming}
              >
                <svg viewBox="0 0 24 24" className="sfa-send-icon" aria-hidden="true">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </form>
          )}

          {config.branding.show_studio_f_branding && (
            <div className="sfa-footer">
              Propulsé par{' '}
              <a href={STUDIO_F_HREF} target="_blank" rel="noopener noreferrer">
                Studio F Agents
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Typing() {
  return (
    <span className="sfa-typing">
      <span />
      <span />
      <span />
    </span>
  );
}

type ConfigErrorBannerProps = {
  error: string;
  mode: WidgetMode;
};

/**
 * Bandeau discret en bas à droite quand le chargement de la config échoue.
 *
 * Le message reste neutre côté visiteur ("intégrateur" plutôt que "client",
 * "configuration" plutôt que "domaine non autorisé") tout en donnant à
 * l'intégrateur une indication précise (status HTTP + hostname + slug).
 *
 * Dismissible : croix de fermeture qui retire le bandeau et stocke en
 * sessionStorage pour que le visiteur ne le revoie pas pendant la session.
 */
function ConfigErrorBanner({ error, mode }: ConfigErrorBannerProps) {
  const dismissKey = `studiof-agents:config-error-dismissed:${modeIdentity(mode)}`;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(dismissKey) === '1';
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  const statusMatch = error.match(/\((\d{3})\)/);
  const status = statusMatch ? Number(statusMatch[1]) : null;

  let reason: string;
  if (status === 403) {
    reason = 'Domaine non autorisé.';
  } else if (status === 404) {
    reason = mode.kind === 'prod' ? 'Agent introuvable.' : 'Démo expirée.';
  } else if (status && status >= 500) {
    reason = 'Service indisponible.';
  } else {
    reason = 'Configuration manquante.';
  }

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(dismissKey, '1');
    } catch {
      // sessionStorage indisponible (Safari private), tant pis : on dismiss en mémoire seulement.
    }
  };

  return (
    <output className="sfa-config-error" aria-live="polite">
      <div className="sfa-config-error-content">
        <strong>Studio F Agents</strong>
        <span className="sfa-config-error-reason">{reason}</span>
        <span className="sfa-config-error-hint">Contactez votre intégrateur.</span>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="sfa-config-error-close"
        aria-label="Fermer ce message"
      >
        ×
      </button>
    </output>
  );
}
